import { Ref, onBeforeUnmount, watch, nextTick } from "vue";

export interface ScrollState {
  position: number;
  messageIndex: number;
  timestamp: number;
  shouldAutoScroll: boolean;
}

const globalScrollCache = new Map<string, ScrollState>();

export function useScrollState(
  conversationId: Ref<string | null>,
  scrollElement: Ref<HTMLElement | null>,
  virtualizer: any
) {
  const saveScrollPosition = () => {
    if (!conversationId.value || !scrollElement.value) return;

    try {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement.value;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      const virtualItems = virtualizer.value?.getVirtualItems();
      const firstVisible = virtualItems && virtualItems.length > 0 ? virtualItems[0] : null;

      globalScrollCache.set(conversationId.value, {
        position: scrollTop,
        messageIndex: firstVisible?.index || 0,
        timestamp: Date.now(),
        shouldAutoScroll: isNearBottom,
      });
    } catch (error) {
      console.error("Error saving scroll position:", error);
    }
  };

  const getScrollState = (): ScrollState | null => {
    if (!conversationId.value) return null;
    return globalScrollCache.get(conversationId.value) || null;
  };

  const restoreScrollPosition = () => {
    const cached = getScrollState();
    if (cached && scrollElement.value && virtualizer.value) {
      nextTick(() => {
        try {
          virtualizer.value.scrollToIndex(cached.messageIndex, {
            align: "start",
            behavior: "auto",
          });
        } catch (error) {
          console.error("Error restoring scroll position:", error);
          if (scrollElement.value) {
            scrollElement.value.scrollTop = cached.position;
          }
        }
      });
    }
  };

  const clearScrollState = () => {
    if (conversationId.value) {
      globalScrollCache.delete(conversationId.value);
    }
  };

  const clearAllScrollStates = () => {
    globalScrollCache.clear();
  };

  onBeforeUnmount(() => {
    saveScrollPosition();
  });

  watch(
    conversationId,
    (newId, oldId) => {
      if (oldId) {
        saveScrollPosition();
      }
      if (newId && scrollElement.value) {
        nextTick(() => {
          restoreScrollPosition();
        });
      }
    },
    { immediate: false }
  );

  return {
    saveScrollPosition,
    getScrollState,
    restoreScrollPosition,
    clearScrollState,
    clearAllScrollStates,
  };
}
